import React, { useContext, useEffect, useState, useMemo } from 'react';
import { CircularProgress, makeStyles } from '@material-ui/core';
import { Issue, NumericMetricInfo, RobotInfo, RCNumericMetricType } from '@moderepo/mode-apis';
import { getRobotDefinitionByRobotClass } from '@moderepo/mode-app-components';
import { fetchIssues, fetchRobotById, fetchRobotCatalog, rcDataContext, selectIssues, selectRobotById, selectRobotCatalog, UIFetchIssueFilters }
    from '@moderepo/mode-data-state-robot-cloud';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { RCRobotsMapContainer } from './RCRobotsMapContainer';
import { RitzComponentsCollection } from '../../utils/ritzComponentsCollection';



const useStyle = makeStyles(() => {
    return {
        map: {
            width : '100%',
            height: '100%',
        },
    };
}, { name: 'RobotCloudProjectDetailsPage' });



// Define the filters here so that new Object doesn't get created every time when we call selectIssues
// which can cause infinite loop. This filters will be used to fetch unresolved issues so that we can show the # of critical/warning issues
const issueFilters: UIFetchIssueFilters = {
    pageNumber: 0, pageSize: 100, resolved: false,
};


interface BasicRobotInfo {
    readonly homeId: number;
    readonly id: string;
}


/**
 * Given an Array of BasicRobotInfo (a minimum robot info which contain only the robot id and homeId), fetch the complete Robot data
 * for each of those robot info and return the Array of complete robot info.
 * @param projectId
 * @param basicRobotsInfo
 */
const useFetchMultipleRobots = (projectId: number, basicRobotsInfo: readonly BasicRobotInfo[] | undefined): readonly RobotInfo[] | undefined => {

    const { state:rcDataState, dispatch:rcDataDispatch } = useContext(rcDataContext);
    const [robots, setRobots] = useState<readonly RobotInfo[] | undefined>();
    const [robotDataLoaded, setRobotLoaded] = useState<boolean>(false);


    // This use effect is used for dispatching actions to load all robot data
    useEffect(() => {
        if (basicRobotsInfo) {
            // Build an Array of dataDispatch and use Promise.all to dispatch them all
            Promise.allSettled(basicRobotsInfo.map((robotInfo: BasicRobotInfo) => {
                return rcDataDispatch(fetchRobotById(projectId, robotInfo.homeId, robotInfo.id), true, true);
            })).then(() => {
                // When all the API calls are done, set the robotLoaded to true so that we can start getting robot data from the global state
                setRobotLoaded(true);
            });
        }
    }, [projectId, basicRobotsInfo, rcDataDispatch]);


    // Wait for all Robot data to be loaded and then create an array of Robots and add it to the local state
    useEffect(() => {
        if (basicRobotsInfo && robotDataLoaded && !robots) {
            // For each BasicRobotInfo, get the complete robot data from the global state and add it to the result and then
            // add the entire result to the local state.
            setRobots(basicRobotsInfo.reduce((result: readonly RobotInfo[], robot: BasicRobotInfo): readonly RobotInfo[] => {
                const robotInfo = selectRobotById(rcDataState, robot.homeId, robot.id);
                if (robotInfo) {
                    return [
                        ...result,
                        robotInfo,
                    ];
                }
                return result;
            }, []));
        }
    }, [robotDataLoaded, robots, basicRobotsInfo, rcDataState]);

    return robots;
};


export interface RCIssueRobotsMapContainerProps {
    readonly projectId: number;
}


/**
 * This container is used for displaying robots that has unresolved issues
 */
export const RCIssueRobotsMapContainer: React.FC<RCIssueRobotsMapContainerProps> = (props: RCIssueRobotsMapContainerProps) => {

    const { state:dataState } = useContext(ritzDataContext);
    const { state:rcDataState, dispatch:rcDataDispatch } = useContext(rcDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const classes = useStyle();
    const robotCatalog = selectRobotCatalog(rcDataState, props.projectId);
    const issues = selectIssues(rcDataState, props.projectId, issueFilters);


    // A list of Robots that have issues that we will display on the Map
    const [robotsWithIssues, setRobotsWithIssues] = useState<readonly BasicRobotInfo[] | undefined>();
    const robots = useFetchMultipleRobots(props.projectId, robotsWithIssues);


    useEffect(() => {

        // If we have Issues data, build a UNIQUE list of robots with issues. Because there cna be multiple issues tied to the same robot, we
        // need to make sure not to include the same robot multiple time in the list
        if (issues) {
            // For each issue, find the robotId that tied to the issue. If a robot has multiple issue, only return 1 robot. The
            // includedHomeIdRobotIdPair will be used to keep track of the homeId/robotId we already included. robotIds are NOT unique per
            // project, they are unique per home. So we need to combine homeId and robotId as 1 string
            const includedHomeIdRobotIdPair: {[homeIdRobotId: string]: boolean} = {};
            const newRobotsWithIssuesList = issues.items.reduce((result: BasicRobotInfo[], issue: Issue): BasicRobotInfo[] => {
                const key = `${issue.homeId}-${issue.robotId}`;
                if (!includedHomeIdRobotIdPair[key]) {
                    includedHomeIdRobotIdPair[key] = true;
                    return [
                        ...result,
                        {
                            homeId: issue.homeId,
                            id    : issue.robotId,
                        },
                    ];
                }
                return result;
            }, []).sort((robot1: BasicRobotInfo, robot2: BasicRobotInfo) => {
                if (robot1.homeId < robot2.homeId) {
                    return -1;
                }
                if (robot1.homeId > robot2.homeId) {
                    return 1;
                }
                if (robot1.id < robot2.id) {
                    return -1;
                }
                if (robot1.id > robot2.id) {
                    return 1;
                }
                return 0;
            });


            // NOTE: make sure the newDevicesWithIssues list is different from the current devicesWithIssues before we update the current list
            // or else this will trigger an update to the devices map and cause it to load device statuses and TSDB data which is expensive.
            // 1 way to check if the 2 list are the same is by JSON.stringify the 2 arrays and see if the strings are the same.
            if (!robotsWithIssues || JSON.stringify(newRobotsWithIssuesList) !== JSON.stringify(robotsWithIssues)) {
                setRobotsWithIssues(Object.values(newRobotsWithIssuesList));
            }
        }
    }, [issues, robotsWithIssues]);


    /**
     * This useEffect is used for loading robot definition if it is not loaded
     */
    useEffect(() => {
        if (!robotCatalog) {
            rcDataDispatch(fetchRobotCatalog(props.projectId)).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [robotCatalog, rcDataDispatch, props.projectId]);


    /**
     * This useEffect is used for loading issues on component load
     */
    useEffect(() => {
        rcDataDispatch(fetchIssues(props.projectId, issueFilters)).catch(() => {
            // ignore errors because dataDispatch already handled it
        });
    }, [props.projectId, rcDataDispatch]);



    /**
     * Filter out robots that don't have GPS metrics so that we don't show them on the map
     */
    const robotsWithGPSMetrics = useMemo(() => {
        if (robotCatalog && robots) {
            return robots.filter((robot: RobotInfo) => {
                const robotDef = getRobotDefinitionByRobotClass(robotCatalog, robot.robotClass);
                if (robotDef) {
                    return robotDef.numericMetrics.find((numericMetric: NumericMetricInfo) => {
                        return numericMetric.type === RCNumericMetricType.LATITUDE || numericMetric.type === RCNumericMetricType.LONGITUDE;
                    }) !== undefined;
                }
                return false;
            });
        }
        return [];
    }, [robotCatalog, robots]);


    const needMap = robotsWithGPSMetrics.length > 0 && robotsWithIssues && robotsWithIssues.length > 0;


    return (
        <>
            {project ? (
                <>
                    {needMap && (
                        <RCRobotsMapContainer
                            className={classes.map}
                            projectId={props.projectId}
                            robots={robotsWithGPSMetrics}
                            defaultZoom={13}
                        />
                    )}
                </>
            ) : (
                <CircularProgress />
            )}
        </>
    );
};

// Register this component with the RitzComponentsCollection so that it can be used by the Custom UI system
RitzComponentsCollection.getInstance().registerComponent('RCIssueRobotsMapContainer', RCIssueRobotsMapContainer);
